/**
 * The terrain: the sea and the land as two viewport-sized quads whose fragment
 * shaders sample the tiles and the masks and blend them in one pass each.
 *
 * The original client stacked tiling sprites: the sea with its bathymetry
 * multiplied over it at 0.3, then forest, sand masked by its sprite and rock
 * masked by its sprite, under the coastline stencil. Pixi 8 draws a sprite
 * mask through its alpha-mask filter, which renders the masked layer to an
 * offscreen texture and combines it with the mask in a second pass, so the
 * land was five screen-sized passes and the sea two. Here each is one, with
 * the same maths:
 *
 *   land = mix(mix(forest, sand, sandMask), rock, rockMask)
 *   sea  = sea * mix(1, seaMask, 0.3)
 *
 * The tiles repeat at a texel per world unit and the masks cover the map at
 * eight units per texel. The masks are clamped half a texel in from their
 * edges as Pixi's mask shader clamps its own, so the land matches the sprites
 * it replaces to the rounding of the intermediate passes, with one deliberate
 * difference: the tiles have mipmaps, as they did in the original client, so
 * the ground no longer sparkles when it scrolls. The masks upload as
 * single-channel textures: the mask filter only ever read their red channel
 * and the files are grey, so nothing is lost, and the three 4096 x 2048 masks
 * take 25 MB of texture memory instead of 100.
 *
 * The tiles wrap in the sampler, not in the shader. A fract() of the tile
 * coordinate puts a jump from 1 to 0 at every tile edge, and where that jump
 * falls inside one of the 2 x 2 pixel quads the GPU takes its derivatives
 * over, the derivative is a whole tile, so the mip selection picks the
 * smallest level, the tile's average colour, for that pixel: a thin line at
 * each seam that comes and goes as the camera moves by fractions of a pixel.
 * Sampling the unwrapped coordinate through a repeating sampler keeps the
 * derivatives continuous across the seam, as Pixi's batched tiling sprite
 * does for a power-of-two texture.
 *
 * Texture coordinates are computed in the vertex shader from the camera's
 * offset within its tile, so the fragment shader never sees a coordinate
 * larger than a few tiles: a mediump fragment shader, where a mobile GPU has
 * no highp, keeps a texel of precision where the original's tiling shader
 * drifted far from the map's centre.
 *
 * Pixi binds its global uniforms (group 0) and the mesh's local uniforms
 * (group 1) to any mesh shader that declares them; the terrain's uniforms and
 * textures are group 2, and the two shaders share one uniform group. The
 * container's tint and alpha are not applied: the terrain is shown or hidden,
 * never faded.
 */
import type { Container, Renderer, Texture } from 'pixi.js';
import { Mesh, MeshGeometry, RendererType, Shader, State, UniformGroup } from 'pixi.js';
import { MAP_CENTER_X, MAP_CENTER_Y, MAP_HEIGHT, MAP_WIDTH } from './Constants';
import { getTexture } from './Textures';

/** The bathymetry's alpha over the sea, as the original's multiply sprite had it. */
const SEA_MASK_ALPHA = 0.3;

/** The masks, read through their red channel alone. */
const MASKS = ['map_sea_mask', 'map_sand_mask', 'map_rock_mask'] as const;

/** The camera and scale, written each frame and read by both shaders. */
const uniforms = new UniformGroup({
  /** The camera's top-left corner within its land tile, in world units. */
  uCameraTile: { value: new Float32Array(2), type: 'vec2<f32>' },
  /** The camera's top-left corner from the map's top-left corner, in world units. */
  uCameraMap: { value: new Float32Array(2), type: 'vec2<f32>' },
  /** World units per canvas pixel. */
  uInvScale: { value: 1, type: 'f32' },
});

/** The land tile's size in world units, the modulus of uCameraTile. */
let landTile = 1;
const geometries: MeshGeometry[] = [];

/** A GLSL or WGSL float literal. */
function f(value: number): string {
  const text = String(value);
  return text.includes('.') || text.includes('e') ? text : `${text}.0`;
}

/** Pixi's half-texel clamp frame for a whole texture, as a vec4 literal. */
function clampFrame(texture: Texture): string {
  const { width, height } = texture;
  return `vec4<f32>(${f(0.5 / width)}, ${f(0.5 / height)}, ${f(1 - 0.5 / width)}, ${f(1 - 0.5 / height)})`;
}

/** The shader sources with the texture sizes baked in. */
function sources(land: Texture, sea: Texture, mask: Texture) {
  const constants = {
    landTile: f(land.width),
    seaTile: f(sea.width),
    mapSize: `(${f(MAP_WIDTH)}, ${f(MAP_HEIGHT)})`,
    maskClamp: clampFrame(mask),
    seaMaskAlpha: f(SEA_MASK_ALPHA),
  };
  const glsl = (wgslVec4: string) => wgslVec4.replace('vec4<f32>', 'vec4');

  const glVertex = /* glsl */ `
    in vec2 aPosition;
    out vec2 vTile;
    out vec2 vSea;
    out vec2 vMap;
    uniform mat3 uProjectionMatrix;
    uniform mat3 uWorldTransformMatrix;
    uniform mat3 uTransformMatrix;
    uniform vec2 uCameraTile;
    uniform vec2 uCameraMap;
    uniform float uInvScale;
    const float LAND_TILE = ${constants.landTile};
    const float SEA_TILE = ${constants.seaTile};
    const vec2 MAP_SIZE = vec2${constants.mapSize};

    void main() {
      mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
      gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
      vec2 units = aPosition * uInvScale;
      vec2 fromTile = uCameraTile + units;
      vTile = fromTile / LAND_TILE;
      vSea = fromTile / SEA_TILE;
      vMap = (uCameraMap + units) / MAP_SIZE;
    }
  `;

  const glLand = /* glsl */ `
    in vec2 vTile;
    in vec2 vMap;
    uniform sampler2D uForest;
    uniform sampler2D uSand;
    uniform sampler2D uRock;
    uniform sampler2D uSandMask;
    uniform sampler2D uRockMask;
    const vec4 MASK_CLAMP = ${glsl(constants.maskClamp)};
    out vec4 finalColor;

    void main() {
      // The tiles wrap in the sampler: no fract, so the derivatives stay continuous at the seams.
      vec3 forest = texture(uForest, vTile).rgb;
      vec3 sand = texture(uSand, vTile).rgb;
      vec3 rock = texture(uRock, vTile).rgb;
      // Past the masks' clamp frame the mask filter drew nothing: forest shows.
      float inMap = step(MASK_CLAMP.x, vMap.x) * step(MASK_CLAMP.y, vMap.y) * step(vMap.x, MASK_CLAMP.z) * step(vMap.y, MASK_CLAMP.w);
      float sandMask = texture(uSandMask, vMap).r * inMap;
      float rockMask = texture(uRockMask, vMap).r * inMap;
      finalColor = vec4(mix(mix(forest, sand, sandMask), rock, rockMask), 1.0);
    }
  `;

  const glSea = /* glsl */ `
    in vec2 vSea;
    in vec2 vMap;
    uniform sampler2D uSea;
    uniform sampler2D uSeaMask;
    const float SEA_MASK_ALPHA = ${constants.seaMaskAlpha};
    out vec4 finalColor;

    void main() {
      vec3 sea = texture(uSea, vSea).rgb;
      // The bathymetry sprite covered the map exactly, multiplied over the sea at its alpha.
      float inMap = step(0.0, vMap.x) * step(0.0, vMap.y) * step(vMap.x, 1.0) * step(vMap.y, 1.0);
      float mask = texture(uSeaMask, vMap).r;
      finalColor = vec4(sea * mix(1.0, mask, SEA_MASK_ALPHA * inMap), 1.0);
    }
  `;

  const gpuCommon = /* wgsl */ `
    struct GlobalUniforms {
      uProjectionMatrix: mat3x3<f32>,
      uWorldTransformMatrix: mat3x3<f32>,
      uWorldColorAlpha: vec4<f32>,
      uResolution: vec2<f32>,
    }
    struct LocalUniforms {
      uTransformMatrix: mat3x3<f32>,
      uColor: vec4<f32>,
      uRound: f32,
    }
    struct TerrainUniforms {
      uCameraTile: vec2<f32>,
      uCameraMap: vec2<f32>,
      uInvScale: f32,
    }
    @group(0) @binding(0) var<uniform> globalUniforms: GlobalUniforms;
    @group(1) @binding(0) var<uniform> localUniforms: LocalUniforms;
    @group(2) @binding(0) var<uniform> terrainUniforms: TerrainUniforms;
    @group(2) @binding(1) var uSampler: sampler;
    const LAND_TILE: f32 = ${constants.landTile};
    const SEA_TILE: f32 = ${constants.seaTile};
    const MAP_SIZE: vec2<f32> = vec2<f32>${constants.mapSize};

    struct VSOutput {
      @builtin(position) position: vec4<f32>,
      @location(0) tile: vec2<f32>,
      @location(1) sea: vec2<f32>,
      @location(2) map: vec2<f32>,
    }

    @vertex
    fn mainVertex(@location(0) aPosition: vec2<f32>) -> VSOutput {
      let mvp = globalUniforms.uProjectionMatrix * globalUniforms.uWorldTransformMatrix * localUniforms.uTransformMatrix;
      let units = aPosition * terrainUniforms.uInvScale;
      let fromTile = terrainUniforms.uCameraTile + units;
      var out: VSOutput;
      out.position = vec4<f32>((mvp * vec3<f32>(aPosition, 1.0)).xy, 0.0, 1.0);
      out.tile = fromTile / LAND_TILE;
      out.sea = fromTile / SEA_TILE;
      out.map = (terrainUniforms.uCameraMap + units) / MAP_SIZE;
      return out;
    }
  `;

  const gpuLand = /* wgsl */ `
    ${gpuCommon}
    @group(2) @binding(2) var uForest: texture_2d<f32>;
    @group(2) @binding(3) var uSand: texture_2d<f32>;
    @group(2) @binding(4) var uRock: texture_2d<f32>;
    @group(2) @binding(5) var uSandMask: texture_2d<f32>;
    @group(2) @binding(6) var uRockMask: texture_2d<f32>;
    const MASK_CLAMP: vec4<f32> = ${constants.maskClamp};

    @fragment
    fn mainFragment(@location(0) vTile: vec2<f32>, @location(1) vSea: vec2<f32>, @location(2) vMap: vec2<f32>) -> @location(0) vec4<f32> {
      let forest = textureSample(uForest, uSampler, vTile).rgb;
      let sand = textureSample(uSand, uSampler, vTile).rgb;
      let rock = textureSample(uRock, uSampler, vTile).rgb;
      let inMap = step(MASK_CLAMP.x, vMap.x) * step(MASK_CLAMP.y, vMap.y) * step(vMap.x, MASK_CLAMP.z) * step(vMap.y, MASK_CLAMP.w);
      let sandMask = textureSample(uSandMask, uSampler, vMap).r * inMap;
      let rockMask = textureSample(uRockMask, uSampler, vMap).r * inMap;
      return vec4<f32>(mix(mix(forest, sand, sandMask), rock, rockMask), 1.0);
    }
  `;

  const gpuSea = /* wgsl */ `
    ${gpuCommon}
    @group(2) @binding(2) var uSea: texture_2d<f32>;
    @group(2) @binding(3) var uSeaMask: texture_2d<f32>;
    const SEA_MASK_ALPHA: f32 = ${constants.seaMaskAlpha};

    @fragment
    fn mainFragment(@location(0) vTile: vec2<f32>, @location(1) vSea: vec2<f32>, @location(2) vMap: vec2<f32>) -> @location(0) vec4<f32> {
      let sea = textureSample(uSea, uSampler, vSea).rgb;
      let inMap = step(0.0, vMap.x) * step(0.0, vMap.y) * step(vMap.x, 1.0) * step(vMap.y, 1.0);
      let mask = textureSample(uSeaMask, uSampler, vMap).r;
      return vec4<f32>(sea * mix(1.0, mask, SEA_MASK_ALPHA * inMap), 1.0);
    }
  `;

  return { glVertex, glLand, glSea, gpuLand, gpuSea };
}

/** A quad of the given size with its corner at the origin. */
function quadPositions(width: number, height: number): Float32Array {
  return new Float32Array([0, 0, width, 0, width, height, 0, height]);
}

/** An opaque quad drawn with the given shader: no blending, nothing behind it shows. */
function quad(shader: Shader): Mesh<MeshGeometry, Shader> {
  const geometry = new MeshGeometry({
    positions: quadPositions(1, 1),
    uvs: new Float32Array(8),
    indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
  });
  geometries.push(geometry);
  const state = State.for2d();
  state.blend = false;
  return new Mesh({ geometry, shader, state });
}

/**
 * Builds the sea and land quads from the loaded textures. The caller parents
 * them, sizes them with resize and scales them with setScale; setCamera moves
 * them each frame.
 */
export function setup(renderer: Renderer): { sea: Container; land: Container } {
  const forest = getTexture('map_forest');
  const sand = getTexture('map_sand');
  const rock = getTexture('map_rock');
  const sea = getTexture('map_sea');
  const seaMask = getTexture('map_sea_mask');
  const sandMask = getTexture('map_sand_mask');
  const rockMask = getTexture('map_rock_mask');
  landTile = forest.width;

  // A single-channel upload needs WebGPU or WebGL2; WebGL1 has no R8.
  const webgl1 = renderer.type === RendererType.WEBGL && 'context' in renderer && renderer.context.webGLVersion === 1;
  if (!webgl1) {
    for (const name of MASKS) {
      getTexture(name).source.format = 'r8unorm';
    }
  }

  // The tiles are drawn smaller than a texel per pixel at every zoom the game
  // uses (a 1080p desktop shows them at 0.6, a phone at 0.25), so without
  // mipmaps the GPU picks one texel in two or four and the ground sparkles as
  // it scrolls. The original client had them: Pixi 4 made mipmaps for every
  // power-of-two texture, and Pixi 8 defaults them off. The masks need none,
  // they are magnified.
  // The tiles wrap in the sampler (see the header). They are powers of two,
  // which WebGL1 needs for repeat. The land shader's one WebGPU sampler is the
  // forest's, so there the masks wrap too; past the map every mask sample is
  // discarded by inMap, so nothing of the far edge shows.
  for (const texture of [forest, sand, rock, sea]) {
    texture.source.autoGenerateMipmaps = true;
    texture.source.style.addressMode = 'repeat';
    texture.source.style.update();
  }

  const source = sources(forest, sea, sandMask);
  const landShader = Shader.from({
    gl: { vertex: source.glVertex, fragment: source.glLand, name: 'terrain-land' },
    gpu: {
      vertex: { source: source.gpuLand, entryPoint: 'mainVertex' },
      fragment: { source: source.gpuLand, entryPoint: 'mainFragment' },
      name: 'terrain-land',
    },
    resources: {
      terrainUniforms: uniforms,
      uSampler: forest.source.style,
      uForest: forest.source,
      uSand: sand.source,
      uRock: rock.source,
      uSandMask: sandMask.source,
      uRockMask: rockMask.source,
    },
  });
  const seaShader = Shader.from({
    gl: { vertex: source.glVertex, fragment: source.glSea, name: 'terrain-sea' },
    gpu: {
      vertex: { source: source.gpuSea, entryPoint: 'mainVertex' },
      fragment: { source: source.gpuSea, entryPoint: 'mainFragment' },
      name: 'terrain-sea',
    },
    resources: {
      terrainUniforms: uniforms,
      uSampler: sea.source.style,
      uSea: sea.source,
      uSeaMask: seaMask.source,
    },
  });

  const seaQuad = quad(seaShader);
  seaQuad.label = 'sea';
  const landQuad = quad(landShader);
  landQuad.label = 'land';
  return { sea: seaQuad, land: landQuad };
}

/** Sizes both quads to the viewport, in canvas pixels. */
export function resize(width: number, height: number): void {
  for (const geometry of geometries) {
    geometry.positions = quadPositions(width, height);
  }
}

/** Applies the world scale, in canvas pixels per world unit. */
export function setScale(scale: number): void {
  uniforms.uniforms.uInvScale = 1 / scale;
}

/** Moves the terrain under the camera, whose top-left corner is (x, y) in world units. */
export function setCamera(x: number, y: number): void {
  const { uCameraTile, uCameraMap } = uniforms.uniforms;
  uCameraTile[0] = x - Math.floor(x / landTile) * landTile;
  uCameraTile[1] = y - Math.floor(y / landTile) * landTile;
  uCameraMap[0] = x + MAP_CENTER_X;
  uCameraMap[1] = y + MAP_CENTER_Y;
}
